import type { CreativeJob } from '@/store/slices/creativeJobs';
import { ATF_CARDS_DRAWS, ATF_CARDS_KIND } from './above-the-fold-api';

/** A cards regeneration in flight: its job and how many of its images have landed. */
export interface AtfCardsRun {
  jobId: string;
  completed: number;
  total: number;
}

const isActive = (job: CreativeJob): boolean => job.status === 'queued' || job.status === 'running';

/** The brand's cards regeneration in flight (the newest, should there be two), or null. Pure. */
export const activeAtfCardsRun = (jobs: Record<string, CreativeJob>, tenantId: number | undefined): AtfCardsRun | null => {
  if (tenantId === undefined) return null;
  const runs = Object.values(jobs)
    .filter((job) => job.kind === ATF_CARDS_KIND && job.tenant_id === String(tenantId) && isActive(job))
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  const run = runs[0];
  if (!run) return null;
  return { jobId: run.id, completed: run.completed ?? 0, total: run.total || ATF_CARDS_DRAWS };
};

/** How each followed run ended, once it has: done, or failed with its owner-readable error. Pure. */
export const settledAtfCardsRuns = (
  jobs: Record<string, CreativeJob>,
  followed: Iterable<string>,
): { jobId: string; done: boolean; error?: string }[] => {
  const settled: { jobId: string; done: boolean; error?: string }[] = [];
  for (const jobId of followed) {
    const job = jobs[jobId];
    if (!job || isActive(job)) continue;
    settled.push(job.status === 'done' ? { jobId, done: true } : { jobId, done: false, error: job.error });
  }
  return settled;
};
